import { useState } from 'react';

import { TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { useParams } from 'react-router';

import {
  useActivateFromReserveMutation,
  useGetTestingParticipantsQuery,
} from '@/api/endpoints/schools/schoolSelectionPanel';
import { ReservedUsersCardList } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/TestingTab/(pages)/_components/ReservedUsersModal/_components/ReservedUsersCardList/ReservedUsersCardList';
import { FetchDataWrapper } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';
import { ETestStatus } from '@/shared/types';

import styles from './ReservedUsersModal.module.scss';

type TProps = {
  isModalOpen: boolean;
  handleCancel: VoidFunction;
};

export const ReservedUsersModal = ({ isModalOpen, handleCancel }: TProps) => {
  const { id: schoolId } = useParams();

  const [reservedUsersIds, setReservedUsersIds] = useState<Set<number>>(() => new Set());
  const isReservedUsers = reservedUsersIds.size > 0;

  const {
    data: testingData,
    isFetching: isTestingFetching,
    isError: isTestingFetchError,
    refetch,
  } = useGetTestingParticipantsQuery({
    schoolId: Number(schoolId),
    page: 1,
    perPage: 999,
    statusFilter: [ETestStatus.InReserve],
  });

  const sendApiRequest = useApiRequest();

  const [activateFromReserve, { isLoading: isActivating }] = useActivateFromReserveMutation();

  const activateFromReserveHandler = async () => {
    await sendApiRequest({
      successMessage: 'Пользователи активированы',
      apiRequestCallback: () =>
        activateFromReserve({
          schoolId: Number(schoolId),
          reservedUsersIds: [...reservedUsersIds],
        }),
      successCallback: handleCancel,
    });
  };

  return (
    <Modal
      width={700}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <FetchDataWrapper
        isModalUse
        isLoading={isTestingFetching}
        isError={isTestingFetchError}
        refreshCb={refetch}
      >
        <div className={styles.wrapper}>
          <div className={styles['title-wrapper']}>
            <TeamOutlined className={styles['title-icon']} />
            <p className={styles.title}>Управление резервом</p>
          </div>
          <p className={styles.description}>
            Выберите резервистов для активации и отправки уведомления о предоставлении документов
          </p>
          {testingData && (
            <ReservedUsersCardList
              reservedTestingData={testingData.data}
              reservedUsersIds={reservedUsersIds}
              setReservedUsersIds={setReservedUsersIds}
            />
          )}
          <p className={styles.annotation}>
            <b className={styles['bold-text']}>Что произойдет:</b> Выбранные резервисты будут переведены в статус
            "Ожидание документов" и получат уведомление о необходимости предоставить документы для продолжения участия в
            отборе
          </p>
          <div className={styles['buttons-wrapper']}>
            <Button onClick={handleCancel}>Отмена</Button>
            <Button
              variant="solid"
              color="green"
              icon={<UserAddOutlined />}
              onClick={activateFromReserveHandler}
              disabled={!isReservedUsers || isActivating}
            >
              Активировать и отправить уведомление
            </Button>
          </div>
        </div>
      </FetchDataWrapper>
    </Modal>
  );
};
